import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, Paper, Stack, TextField, Typography } from "@mui/material";

import { byTag, explain, kindOf, normaliseMissing, requestFor, whoText } from "../access.js";
import { KernelLockup } from "../brand.jsx";
import { freshToken, kernel, kernelPost, renewToken, sandboxConfig } from "../client.js";
import Disclose from "./Disclose.jsx";
import ErrorState from "./ErrorState.jsx";

/**
 * The missing-permissions screen (LO-209): what a person is shown **instead of
 * an app** when they do not hold every tag the app's approved manifest uses.
 *
 * ## The rule it speaks for
 *
 * A person may open an app only if they hold every tag the app reads or writes
 * with. It used to be a partial view, `manifest ∩ user`, and an app that opened
 * and showed less told a person nothing about why. The kernel now refuses the
 * whole app, and this is the page that refusal arrives as: it names exactly what
 * is missing, from the kernel's own `missing` list and never from a copy of the
 * vocabulary, says who decides, and lets the person ask.
 *
 * ## What it says, and what it does not
 *
 * A refusal is information. It is worded as a fact about what the person holds
 * ("You don't hold finance"), never as "no results" or "something went wrong",
 * because an empty page and a refused app are different sentences about
 * different people's decisions. It is also careful about what it does *not*
 * know: it says what the person holds today, not whether they should hold it,
 * and once somebody has asked it says that asking grants nothing by itself. The
 * kernel's own `does_not_prove` sits one click in, on the claim, not at the foot
 * of the page.
 *
 * ## Where it is used, and what the sandbox QA reads off it (LO-211)
 *
 * `AccessGate` shows it in place of the app; `Refusal` shows it where an app's
 * own error handling met the refusal. These attributes are a contract and are
 * documented in the README: the root carries `data-kernel-access-required`, is a
 * named `region`; each missing entry is an element with `data-missing-type`,
 * `data-missing-tag` and `data-missing-access`; each request button carries
 * `data-kernel-request-access` and `data-tag`. `remote-auth`'s plain-DOM shim
 * emits the same.
 *
 * ## Getting back in
 *
 * A request is `POST /apps/{app}/access-requests`; what the person has asked for
 * is `my-access`'s `requests`. The screen looks again by itself while one is
 * pending and whenever the person asks, and when the kernel says they now hold
 * everything it calls `onAllowed` exactly once. A person's tags ride on their
 * token, so an approval is not in the token they are holding: before it looks
 * again for an approved request that is still missing, it renews the session
 * once, which is the reload dance the screen exists to spare them.
 *
 * Props: `appType`, `title` (the app's name for people), `missing` (the
 * kernel's list), `requests` (what `my-access` said they asked for; leave it
 * out and the screen asks), `detail` and `doesNotProve` (the kernel's words),
 * `canRequest`, `token` (a session token or a transport; the session's own when
 * left out), `onAllowed`, `onSignOut`, `pollMs` (0 turns the polling off), and
 * `refusedAnyway` (the app was refused again straight after the kernel said the
 * person could open it, so the screen says that instead of trying again).
 */

const KIND = {
  none: { word: "Not requested", colour: "kernel.stale" },
  pending: { word: "Waiting", colour: "kernel.attention" },
  approved: { word: "Approved", colour: "kernel.permitted" },
  declined: { word: "Declined", colour: "kernel.refused" },
  other: { word: "Requested", colour: "kernel.stale" },
};

const enc = encodeURIComponent;

/** A caveat the kernel sent as one sentence or as a list of them, as a list. */
const sentences = (value) =>
  [].concat(value ?? []).filter((s) => typeof s === "string" && s.trim());

/**
 * A ring on a keyboard-focused button that nothing can reset. MUI's own
 * indication for a button is a pulsing ripple, which is easy to miss on a page
 * whose only job is to be acted on, and the base stylesheet's
 * `:focus-visible` rule loses to ButtonBase's `outline: 0`. Keyboard only,
 * and in the brand colour of whichever scheme the page is in.
 */
const ring = (theme) => ({
  "&.Mui-focusVisible": {
    outline: `2px solid ${(theme.vars || theme).palette.primary.main}`,
    outlineOffset: 2,
  },
});

/** The session's own token when the caller gave none; null rather than a throw
 *  when there is no config to read one from, so the request fails as a 401. */
async function sessionToken() {
  try {
    return await freshToken();
  } catch {
    return null;
  }
}

/**
 * The state as a word in a chip. The colour is the border and the word is in
 * the text colour: text in `kernel.attention` is under 4.5:1 on white, and a
 * state that was only that colour would not read for everyone anyway.
 */
function StateChip({ kind, label }) {
  const { word, colour } = KIND[kind];
  return (
    <Chip
      size="small"
      variant="outlined"
      label={label || word}
      sx={{ borderColor: colour, color: "text.primary", borderWidth: 1, borderStyle: "solid" }}
    />
  );
}

export default function AccessRequired({
  appType,
  title,
  missing,
  requests,
  detail,
  doesNotProve,
  canRequest = true,
  token,
  onAllowed,
  onSignOut,
  pollMs = 15000,
  refusedAnyway = false,
}) {
  const headId = useId();
  const headRef = useRef(null);

  // The kernel's latest answer to my-access, once there is one; until then the
  // screen is drawn from what it was given.
  const [answer, setAnswer] = useState(null);
  // Requests sent from this screen, until my-access lists them.
  const [local, setLocal] = useState([]);
  // What the kernel said about each request when it was filed (who it was
  // routed to, and what filing it does not prove); only the POST says, and
  // `my-access` does not repeat it, so it is kept by request id.
  const [notices, setNotices] = useState({});
  // Tags the kernel says the person's token already carries, with its words.
  const [held, setHeld] = useState({});
  const [sending, setSending] = useState(null);
  const [failed, setFailed] = useState({});
  const [notes, setNotes] = useState({});
  const [checking, setChecking] = useState(false);
  const [checkFailure, setCheckFailure] = useState(null);

  const renewed = useRef(null);
  const renewNext = useRef(false);
  const told = useRef(false);
  const handled = useRef(new Set());
  const onAllowedRef = useRef(onAllowed);
  onAllowedRef.current = onAllowed;

  // A new refusal for a different list is a new screen: drop what the last
  // my-access said about the old one.
  const missingKey = JSON.stringify(missing ?? null);
  useEffect(() => {
    setAnswer(null);
  }, [missingKey]);

  const items = useMemo(() => normaliseMissing(answer ? answer.missing : missing), [answer, missing]);
  const groups = useMemo(() => byTag(items), [items]);
  const listed = answer ? answer.requests : requests;
  const mine = useMemo(
    () => [...(listed || []), ...local.filter((l) => !(listed || []).some((r) => r.id === l.id))],
    [listed, local],
  );
  const kinds = groups.map((g) => ({ tag: g.tag, request: requestFor(mine, g.tag) }));
  const anyPending = kinds.some((k) => kindOf(k.request) === "pending");
  const approvedStill = kinds.filter((k) => kindOf(k.request) === "approved");
  const allowed = Boolean(answer?.ok);

  const approvedStillRef = useRef(approvedStill);
  approvedStillRef.current = approvedStill;

  const bearer = useCallback(
    async () => renewed.current || token || (await sessionToken()),
    [token],
  );
  const read = useCallback(
    async (path) => kernel(await bearer())(path),
    [bearer],
  );

  // `loud` is a person asking. A look the screen takes by itself says nothing if
  // it fails (the next one, or the person, will try again); one the person
  // asked for must not fail silently.
  const recheck = useCallback(async (loud = false) => {
    setChecking(true);
    try {
      const current = renewed.current || token;
      if (
        (approvedStillRef.current.length || renewNext.current) &&
        (!current || typeof current === "string")
      ) {
        // Approved, or already held, and the kernel still refuses: the token in
        // hand predates the grant, because a person's tags ride on it. Renew
        // the session once and ask with the new one. A transport (a host whose
        // server holds the session) renews itself, so it is left.
        renewNext.current = false;
        const next = await renewToken(current || undefined);
        if (typeof next === "string") renewed.current = next;
      }
      const a = await read(`/apps/${enc(appType)}/my-access`);
      setAnswer(a);
      // What the kernel lists is the truth about this app. A request it cannot
      // list — one filed through another app, which `my-access` does not show —
      // is kept until it lists one for that tag.
      setLocal((l) => l.filter((x) => x.sticky && !(a?.requests || []).some((r) => r.tag === x.tag)));
      setCheckFailure(null);
      if (a?.ok && !told.current) {
        told.current = true;
        onAllowedRef.current?.(a);
      }
      return a;
    } catch (e) {
      if (loud) setCheckFailure(e);
      return null;
    } finally {
      setChecking(false);
    }
  }, [appType, read, token]);
  const recheckRef = useRef(recheck);
  recheckRef.current = recheck;

  // The page changed under the person: put them at its heading so a screen
  // reader says what happened, not where it was.
  useEffect(() => {
    headRef.current?.focus();
  }, []);

  // Handed no list of what they asked for (a refusal from a data call, rather
  // than my-access), ask.
  useEffect(() => {
    if (requests === undefined) recheckRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While something is pending, look again by itself, and when the person comes
  // back to the tab: the approver is a person with their own timetable.
  useEffect(() => {
    if (!pollMs || !anyPending) return undefined;
    const look = () => {
      if (!document.hidden) recheckRef.current();
    };
    const id = setInterval(look, pollMs);
    document.addEventListener("visibilitychange", look);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", look);
    };
  }, [pollMs, anyPending]);

  // An approval has arrived but this person's token does not carry it yet: do
  // the renewing and the looking once for each request, not on every render.
  useEffect(() => {
    const fresh = approvedStill.filter((k) => k.request?.id && !handled.current.has(k.request.id));
    if (!fresh.length || allowed) return;
    fresh.forEach((k) => handled.current.add(k.request.id));
    recheckRef.current();
  });

  const send = async (tag) => {
    setSending(tag);
    setFailed((f) => ({ ...f, [tag]: null }));
    const note = (notes[tag] || "").trim();
    try {
      const post = kernelPost(await bearer());
      const created = await post(`/apps/${enc(appType)}/access-requests`, { tag, ...(note ? { note } : {}) });
      const id = created?.id ?? `sent-${tag}`;
      setLocal((l) => [
        ...l.filter((x) => x.tag !== tag),
        { id, tag, state: created?.state || "pending", routed_to: created?.routed_to },
      ]);
      setNotices((n) => ({
        ...n,
        [id]: { routed: created?.routed_to, caveats: sentences(created?.does_not_prove) },
      }));
    } catch (e) {
      if (e?.status === 409 && e?.error === "request_pending") {
        // Already waiting: that is the state the person wanted, not an error.
        // One request covers every app, so it may have been filed through
        // another, which `my-access` for this one will not list: the kernel's
        // own sentence says so and is kept with it.
        setLocal((l) => [
          ...l.filter((x) => x.tag !== tag),
          { id: e.body?.request_id || `sent-${tag}`, tag, state: "pending", sticky: true, detail: e.message },
        ]);
        await recheckRef.current();
      } else if (e?.status === 409 && e?.error === "already_held") {
        // The kernel says the token already carries the tag, so the app is
        // refusing a token older than the grant. Say its words, renew the
        // session and look again.
        setHeld((h) => ({ ...h, [tag]: e.message }));
        renewNext.current = true;
        await recheckRef.current();
      } else {
        setFailed((f) => ({ ...f, [tag]: e }));
      }
    } finally {
      setSending(null);
    }
  };

  const { what, lack } = explain(items);
  const name = title || appType;
  const caveats = sentences(doesNotProve);
  // In a sandbox the banner is fixed over the foot of the viewport (up to three
  // lines at 390px), so the last control needs room above it.
  const room = sandboxConfig() ? 16 : undefined;

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={headId}
      data-kernel-access-required="true"
      data-app-type={appType}
      sx={{ p: { xs: 2, sm: 4 }, pb: room, minWidth: 0 }}
    >
      <Stack spacing={3} sx={{ maxWidth: 680, mx: "auto", minWidth: 0 }}>
        <KernelLockup height={22} />

        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Typography
            id={headId}
            ref={headRef}
            tabIndex={-1}
            variant="h1"
            component="h1"
            sx={{ outline: "none", overflowWrap: "anywhere" }}
          >
            {allowed ? `You can open ${name} now` : `You can't open ${name} yet`}
          </Typography>

          {allowed ? (
            <Typography variant="body1" role="status">
              {refusedAnyway
                ? "The kernel says you now hold everything this app needs, but it refused the app's last request anyway. Reload the page; if this comes back, sign out and in again."
                : "You now hold everything this app needs. Open it again."}
            </Typography>
          ) : (
            <>
              <Typography variant="body1">
                {what} {lack}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Kernel opens an app only for people who hold every tag it uses.
              </Typography>
              {detail ? (
                <Typography variant="body2" color="text.secondary">
                  {detail}
                </Typography>
              ) : null}
              {/* On the claim, not at the foot of the page: this is a fact
                  about what the person holds today and the screen has no view
                  on whether they should. The kernel's own caveat is the detail. */}
              {caveats.length ? (
                <Disclose
                  summary="This says what you hold today, not whether you should hold it."
                  label="What this does not prove"
                >
                  <Stack spacing={1} sx={{ maxWidth: "62ch" }}>
                    {caveats.map((c) => (
                      <Typography key={c} variant="body2" color="text.secondary">
                        {c}
                      </Typography>
                    ))}
                  </Stack>
                </Disclose>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This says what you hold today, not whether you should hold it.
                </Typography>
              )}
            </>
          )}
        </Stack>

        {allowed
          ? null
          : groups.map((group) => {
              const request = requestFor(mine, group.tag);
              const kind = kindOf(request);
              const notice = request ? notices[request.id] : null;
              const who = request ? whoText(request.routed_to) || whoText(notice?.routed) : "";
              const holds = held[group.tag];
              const askable =
                canRequest && !holds && (kind === "none" || kind === "declined" || kind === "other");
              // What is said about this tag, as sentences: the state in words,
              // and then the kernel's own words wherever it gave some.
              const lines = [];
              if (holds) {
                lines.push(holds);
              } else if (kind === "pending") {
                lines.push(`Requested, waiting for ${who || `whoever can grant ${group.tag}`}.`);
                if (request.detail) lines.push(request.detail);
                // Once somebody has acted is where what it did and did not do
                // belongs, and the kernel's sentences are the true ones.
                lines.push(
                  ...(notice?.caveats?.length
                    ? notice.caveats
                    : ["Asking grants nothing by itself, and whoever decides may say no."]),
                );
              } else if (kind === "approved") {
                lines.push(
                  `Approved. It has not reached your sign-in yet, so the kernel still refuses. Check again in a moment${onSignOut ? ", or sign out and back in" : ""}.`,
                );
              } else if (kind === "declined") {
                lines.push(`Declined${request.reason ? `: ${request.reason}` : ""}. You can ask again.`);
              } else if (kind === "other") {
                lines.push(`Your earlier request is ${String(request.state)}.`);
              }
              return (
                <Paper
                  key={group.tag}
                  variant="outlined"
                  data-tag-group={group.tag}
                  sx={{ p: 2, minWidth: 0 }}
                >
                  <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: "center", flexWrap: "wrap", minWidth: 0 }}
                    >
                      <Typography variant="mono" component="span" sx={{ overflowWrap: "anywhere", fontWeight: 600 }}>
                        {group.tag}
                      </Typography>
                      <StateChip kind={kind} label={kind === "other" ? String(request.state) : undefined} />
                    </Stack>

                    <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5, minWidth: 0 }}>
                      {group.items.map((item) => (
                        <Typography
                          component="li"
                          variant="body2"
                          key={`${item.type}/${item.access}`}
                          data-missing-type={item.type}
                          data-missing-tag={item.tag}
                          data-missing-access={item.access}
                          sx={{ overflowWrap: "anywhere" }}
                        >
                          <strong>{item.type}</strong> records tagged{" "}
                          <Typography variant="mono" component="span">{item.tag}</Typography>
                          {" · "}
                          {item.access === "write" ? "write" : "read"}
                        </Typography>
                      ))}
                    </Stack>

                    {/* Always in the page, so a screen reader hears the text
                        arrive; and no room taken while it is empty. */}
                    <Box role="status" sx={{ mt: lines.length ? 0 : -1.5 }}>
                      {lines.map((line) => (
                        <Typography key={line} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {line}
                        </Typography>
                      ))}
                    </Box>

                    {failed[group.tag] ? <ErrorState failure={failed[group.tag]} severity="warning" title="That request was not accepted" /> : null}

                    {askable ? (
                      <Stack spacing={1.5} sx={{ minWidth: 0 }}>
                        <TextField
                          size="small"
                          fullWidth
                          label="Note for whoever decides (optional)"
                          value={notes[group.tag] || ""}
                          onChange={(e) => setNotes((n) => ({ ...n, [group.tag]: e.target.value }))}
                          slotProps={{ htmlInput: { maxLength: 500 } }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Whoever can grant {group.tag} decides. Kernel sends your request to them.
                        </Typography>
                        <Button
                          variant="contained"
                          data-kernel-request-access="true"
                          data-tag={group.tag}
                          disabled={sending === group.tag}
                          onClick={() => send(group.tag)}
                          sx={[ring, { alignSelf: { xs: "stretch", sm: "flex-start" } }]}
                        >
                          {sending === group.tag
                            ? "Sending…"
                            : kind === "declined"
                              ? `Request again for ${group.tag}`
                              : `Request access to ${group.tag}`}
                        </Button>
                      </Stack>
                    ) : null}

                    {!canRequest && kind === "none" ? (
                      <Typography variant="body2" color="text.secondary">
                        This can&apos;t be requested here. Ask whoever manages access to this app.
                      </Typography>
                    ) : null}
                  </Stack>
                </Paper>
              );
            })}

        {checkFailure ? (
          <ErrorState failure={checkFailure} severity="warning" title="Could not check your access" />
        ) : null}

        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: "wrap", alignItems: "center", minWidth: 0 }}
        >
          <Button variant="outlined" sx={ring} disabled={checking} onClick={() => recheck(true)} aria-busy={checking}>
            Check again
          </Button>
          {onSignOut ? (
            <Button variant="text" sx={ring} onClick={onSignOut}>
              Sign out
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}
